import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withStyles, createStyles } from '@material-ui/core/styles';
import { detect } from 'detect-browser';
import { Grid, Typography, Button } from '@material-ui/core';

import { createYesElfIntegrationScript } from 'yeself-common/utils/IntegrationUtils';

import AgentIntegrationForm from 'yeself-client-common/form/components/AgentIntegrationForm';

import EnvironmentConfiguration from 'yeself-client-common/EnvironmentConfiguration';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectAppId,
    selectApplicationIntegrationAgentSettings,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import { pushNotificationToStack } from 'Console/data/notification/actions';
import { SUPPORTED_BROWSERS } from 'Console/constants/appConstants';

import ExperimentWithoutIntegration from './ExperimentWithoutIntegration';
import ScriptElement from './ScriptElement';

const styles = createStyles((theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
    },
    title: {
        display: 'inline-block',
        fontWeight: 'bold',
        marginBottom: theme.typography.pxToRem(20),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        marginBottom: theme.typography.pxToRem(25),
    },
    hiddenInput: {
        position: 'absolute',
        left: 10000,
        top: 10000,
    },
    positionInput: {
        overflow: 'hidden',
        position: 'relative',
    },
    copyButton: {
        marginRight: theme.typography.pxToRem(18),
    },
}));

const Integration = ({ classes, intl }) => {
    const dispatch = useDispatch();

    const applicationId = useSelector(selectAppId);
    const agentIntegration = useSelector(selectApplicationIntegrationAgentSettings);

    const [integrationValue, setIntegrationValue] = useState(agentIntegration);

    const copyToClipboard = useCallback(
        (id) => () => {
            const copyText = document.getElementById(id);

            if (copyText instanceof HTMLInputElement || copyText instanceof HTMLTextAreaElement) {
                copyText.focus();
                copyText.select();
                document.execCommand('copy');

                if (id === 'clipboardInput') {
                    dispatch(
                        pushNotificationToStack(
                            `${intl.formatMessage({ id: 'Notification.script.copied' })}`
                        )
                    );
                } else {
                    dispatch(
                        pushNotificationToStack(
                            `${intl.formatMessage({ id: 'Notification.configuration.copied' })}`
                        )
                    );
                }
            }
        },
        [dispatch, intl]
    );

    const openPluginNewTab = useCallback(() => {
        const plugin =
            'https://chrome.google.com/webstore/detail/yeself-extension/fjajjfnphfjeonfjggjnhnodcaaoaeah';
        window.open(plugin, '_blank');
    }, []);

    const handleSaveSettings = useCallback(() => {
        dispatch(
            updateDetailSettings(
                {
                    'metadata.integration.agent': integrationValue,
                },
                {
                    success: (
                        <FormattedMessage
                            id="Notification.save"
                            defaultMessage="Your settings were saved"
                        />
                    ),

                    fail: (
                        <FormattedMessage
                            id="Notification.fail"
                            defaultMessage="Unable to save, something went wrong"
                        />
                    ),
                }
            )
        );
    }, [dispatch, integrationValue]);

    useEffect(() => {
        setIntegrationValue(agentIntegration);
    }, [agentIntegration]);

    const browser = detect();

    const agentScriptBaseUrl = EnvironmentConfiguration.AGENT_SCRIPTS_BASE_URL;
    const openId = EnvironmentConfiguration.OPEN_ID.url;
    const wssUrl = EnvironmentConfiguration.WEBSOCKET_URL;
    const CSPText = `${
        '<meta http-equiv="Content-Security-Policy" \n' +
        "\t content=\"default-src 'none'; script-src 'self' " +
        `${agentScriptBaseUrl}` +
        ' ' +
        'unsafe-eval; \n' +
        "\t font-src 'self' " +
        `${agentScriptBaseUrl}` +
        ' ' +
        'data: https://fonts.gstatic.com; \n' +
        "\t connect-src 'self' " +
        `${openId} ${agentScriptBaseUrl} ${wssUrl}` +
        '; \n' +
        "\t img-src 'self' " +
        `${agentScriptBaseUrl}` +
        ' ' +
        ' data:; \n' +
        "\t style-src 'self' 'unsafe-inline'" +
        ' ' +
        `${agentScriptBaseUrl}` +
        ' https://fonts.googleapis.com; \n' +
        "\t media-src 'self'; frame-src " +
        `${openId} ${agentScriptBaseUrl}` +
        '; \n' +
        "\t prefetch-src 'self' " +
        `${agentScriptBaseUrl}` +
        ';" \n' +
        '>'
    }`;

    const scriptTag = `<script>\n\t${createYesElfIntegrationScript({
        applicationId,
        url: EnvironmentConfiguration.AGENT_SCRIPTS_BASE_URL,
        ifcNamespace: integrationValue.ifcNamespace,
        baseUrlSlug: EnvironmentConfiguration.SCRIPTS_BASE_SLUG,
    })}</script>`;

    return (
        <Grid container className={classes.root}>
            <Grid container>
                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage
                            id="Settings.integration.title.frameworkSupport"
                            defaultMessage="Framework support"
                        />
                    </Typography>

                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage
                            id="Settings.integration.message3"
                            defaultMessage="Adopt needs to know key information about internal components of your space in order to provide best integrated experience. Based on such information we provide you appropriate integration script."
                        />
                    </Typography>
                    <AgentIntegrationForm value={integrationValue} onChange={setIntegrationValue} />
                    <Button
                        id="yeself-button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveSettings}
                    >
                        <FormattedMessage id="Buttons.save" defaultMessage="Save" />
                    </Button>
                </Grid>
                <Grid item>
                    <Typography
                        variant="subtitle1"
                        className={classes.title}
                        style={{ marginTop: '3.375rem' }}
                    >
                        <FormattedMessage
                            id="Settings.navigation.integration"
                            defaultMessage="Integration"
                        />
                    </Typography>

                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage
                            id="Settings.integration.message"
                            defaultMessage="Integrating Adopt is as easy as placing a script into your space template. We recommend you to place it at the end, before closing BODY tag"
                        />
                    </Typography>

                    <ScriptElement scriptText={scriptTag} />

                    <div className={classes.positionInput}>
                        <input
                            className={classes.hiddenInput}
                            type="text"
                            value={scriptTag}
                            id="clipboardInput"
                        />
                    </div>

                    <Grid container direction="row">
                        <Typography>
                            <Button
                                id="yeself-button"
                                variant="contained"
                                color="primary"
                                onClick={copyToClipboard('clipboardInput')}
                                className={classes.copyButton}
                            >
                                <FormattedMessage id="Buttons.copyScript" defaultMessage="Copy script" />
                            </Button>
                        </Typography>
                    </Grid>

                    <Typography
                        variant="body1"
                        className={classes.message}
                        style={{ marginTop: '3.375rem' }}
                    >
                        <FormattedMessage
                            id="Settings.integration.message4"
                            defaultMessage="If you secure Content Security Policy in space, system or project, you have to include following configuration as well"
                        />
                    </Typography>

                    <ScriptElement scriptText={CSPText} />

                    <div className={classes.positionInput}>
                        <input
                            className={classes.hiddenInput}
                            type="text"
                            value={CSPText}
                            id="clipboardCSP"
                        />
                    </div>

                    <Grid container direction="row">
                        <Typography>
                            <Button
                                id="yeself-button"
                                variant="contained"
                                color="primary"
                                onClick={copyToClipboard('clipboardCSP')}
                                className={classes.copyButton}
                            >
                                <FormattedMessage
                                    id="Buttons.copyConfiguration"
                                    defaultMessage="Copy configuration"
                                />
                            </Button>
                        </Typography>
                    </Grid>
                </Grid>

                <>
                    <Typography
                        variant="subtitle1"
                        className={classes.title}
                        style={{ marginTop: '3.375rem' }}
                    >
                        <FormattedMessage
                            id="Settings.integration.browserExtension"
                            defaultMessage="Browser extension"
                        />
                    </Typography>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage
                            id="Settings.integration.message2"
                            defaultMessage="Adopt is authoring tool that allows you to create virtual tours tailored for your space. In standard case when you have integrated Adopt by following steps above there is no need to worry anymore. But there are cases when you want to try out Adopt capabilities, you are not technically experienced and do not have means to integrate it yet. In such case please install Chrome Extension."
                        />
                    </Typography>
                    <Grid item>
                        <Typography>
                            <Button
                                id="yeself-button"
                                variant="contained"
                                color="primary"
                                onClick={openPluginNewTab}
                            >
                                <FormattedMessage
                                    id="Buttons.open&install"
                                    defaultMessage="Open & install"
                                />
                            </Button>
                        </Typography>
                    </Grid>
                </>

                <Grid>
                    {browser &&
                    SUPPORTED_BROWSERS.some(
                        (supportedBrowser) => supportedBrowser === browser.name.toLowerCase()
                    ) ? (
                        <ExperimentWithoutIntegration
                            browser={browser.name.toLowerCase()}
                            selectedApplication={applicationId}
                        />
                    ) : null}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(Integration));
